import React, { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Lock, Globe, Eye, ArrowRight, ArrowLeft, FileCode, Sparkles } from 'lucide-react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const VisibilityBadge = ({ v }) => {
  if (v === 'private') {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-[#F59E0B]/15 border border-[#F59E0B]/40 text-[#F59E0B] font-mono uppercase tracking-wider">
        <Lock className="w-3 h-3" strokeWidth={2} /> Private
      </span>
    );
  }
  if (v === 'partial') {
    return (
      <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-amber-900/20 border border-amber-600/50 text-amber-400 font-mono uppercase tracking-wider">
        <Eye className="w-3 h-3" strokeWidth={2} /> Preview
      </span>
    );
  }
  return (
    <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 bg-[#0077B6]/15 border border-[#0077B6]/40 text-[#0077B6] font-mono uppercase tracking-wider">
      <Globe className="w-3 h-3" strokeWidth={2} /> Public
    </span>
  );
};

export function TutorialList() {
  const [tutorials, setTutorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    axios.get(`${API}/tutorials`, { withCredentials: true })
      .then(r => {
        if (cancelled) return;
        setTutorials(Array.isArray(r.data) ? r.data : []);
      })
      .catch(() => { if (!cancelled) setError('Could not load tutorials right now.'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, []);

  return (
    <div data-testid="tutorials-list-page" className="min-h-screen bg-[#0A0A0A] text-white">
      <SEO
        title="Interactive Tutorials — Hands-on AI/ML Lessons"
        description="Browser-based interactive tutorials in AI, ML, and Data Science. Live charts, code walkthroughs, and quizzes. Free to explore — sign in for the full library."
        path="/tutorials"
      />
      <div className="max-w-6xl mx-auto px-6 py-16">
        <div className="mb-12">
          <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#F59E0B] mb-3">Learn / Tutorials</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-light mb-4" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
            Interactive Tutorials
          </h1>
          <p className="text-base text-zinc-400 max-w-2xl">
            Hands-on, browser-based lessons in AI, ML, and data science — explore concepts visually with live charts, code, and quizzes. New tutorials are added regularly.
          </p>
        </div>

        {loading && (
          <div data-testid="tutorials-loading" className="text-zinc-400 py-12 text-center">Loading tutorials…</div>
        )}

        {!loading && error && (
          <div data-testid="tutorials-error" className="text-red-400 py-12 text-center">{error}</div>
        )}

        {!loading && !error && tutorials.length === 0 && (
          <div data-testid="tutorials-empty" className="border border-[#262626] bg-[#111111] p-12 text-center">
            <Sparkles className="w-10 h-10 mx-auto text-zinc-600 mb-3" strokeWidth={1.5} />
            <p className="text-zinc-400">No tutorials published yet. Check back soon.</p>
          </div>
        )}

        {!loading && tutorials.length > 0 && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="tutorials-grid">
            {tutorials.map((t, i) => (
              <Link
                key={t.id}
                to={`/tutorials/${t.slug}`}
                data-testid={`tutorial-card-${i}`}
                className="group block border border-[#262626] bg-[#111111] hover:border-[#0077B6] transition-all duration-200 p-6"
              >
                <div className="flex items-start justify-between gap-3 mb-4">
                  <FileCode className="w-7 h-7 text-[#0077B6] flex-shrink-0" strokeWidth={1.5} />
                  <VisibilityBadge v={t.visibility} />
                </div>
                <h2 className="text-lg font-medium text-white mb-2 group-hover:text-[#0077B6] transition-colors" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>
                  {t.title}
                </h2>
                {t.description && (
                  <p className="text-sm text-zinc-400 leading-relaxed line-clamp-3 mb-4">{t.description}</p>
                )}
                {t.tags && t.tags.length > 0 && (
                  <div className="flex flex-wrap gap-1 mb-4">
                    {t.tags.slice(0, 6).map((tag, j) => (
                      <span key={j} className="text-xs font-mono px-1.5 py-0.5 bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/30">{tag}</span>
                    ))}
                  </div>
                )}
                <div className="flex items-center justify-between pt-3 border-t border-[#262626]">
                  <span className="text-xs text-zinc-500 font-mono">{Math.round((t.size || 0) / 1024)} KB</span>
                  <span className="text-sm text-[#0077B6] flex items-center gap-1 group-hover:gap-2 transition-all">
                    Open <ArrowRight className="w-4 h-4" strokeWidth={2} />
                  </span>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export function TutorialView() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [iframeHeight, setIframeHeight] = useState(900);
  const iframeRef = useRef(null);
  const [showLockedToast, setShowLockedToast] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setLoading(true); setError('');
    axios.get(`${API}/tutorials/${slug}`, { withCredentials: true })
      .then(r => {
        if (cancelled) return;
        if (r.data && typeof r.data === 'object' && r.data.title) {
          setMeta(r.data);
        } else {
          setError('Tutorial not found.');
        }
      })
      .catch(err => {
        if (cancelled) return;
        if (err.response?.status === 404) setError('Tutorial not found.');
        else setError('Could not load this tutorial.');
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [slug]);

  // Listen for locked-tab clicks from the iframe (partial mode)
  useEffect(() => {
    const onMsg = (e) => {
      if (!e.data || e.data.type !== 'ioa-tutorial-locked') return;
      setShowLockedToast(true);
      setTimeout(() => setShowLockedToast(false), 4000);
    };
    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, []);

  // Keep iframe tall enough for content
  const onIframeLoad = () => {
    try {
      const doc = iframeRef.current?.contentDocument;
      if (doc) {
        const h = Math.max(doc.body?.scrollHeight || 0, doc.documentElement?.scrollHeight || 0, 700);
        setIframeHeight(Math.min(h + 40, 5000));
      }
    } catch {}
  };

  if (loading) {
    return <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center text-zinc-400" data-testid="tutorial-loading">Loading tutorial…</div>;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6" data-testid="tutorial-error">
        <div className="text-center max-w-md">
          <h1 className="text-2xl font-medium mb-3">Tutorial unavailable</h1>
          <p className="text-zinc-400 mb-6">{error}</p>
          <Link to="/tutorials" className="brand-btn text-sm px-5 py-2 inline-flex items-center gap-2">
            <ArrowLeft className="w-4 h-4" /> Back to tutorials
          </Link>
        </div>
      </div>
    );
  }

  // Private + not authed → server returns requires_login=true with minimal metadata
  if (meta?.requires_login || meta?.visibility === 'private') {
    // If the server returned full meta but visibility is private, the user IS logged in (server already validated).
    // Only show login wall when requires_login flag is true.
    if (meta.requires_login) {
      return (
        <div className="min-h-screen bg-[#0A0A0A] text-white flex items-center justify-center px-6" data-testid="tutorial-private-wall">
          <div className="max-w-lg border border-[#262626] bg-[#111111] p-10 text-center">
            <Lock className="w-10 h-10 mx-auto text-[#F59E0B] mb-4" strokeWidth={1.5} />
            <p className="text-xs font-mono uppercase tracking-widest text-[#F59E0B] mb-2">Members only</p>
            <h1 className="text-2xl font-medium mb-3" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{meta.title}</h1>
            <p className="text-sm text-zinc-400 mb-6">{meta.description || 'Sign in to access this private tutorial.'}</p>
            <div className="flex items-center justify-center gap-3">
              <Link to="/login" className="brand-btn text-sm px-5 py-2" data-testid="tutorial-login-btn">Sign in</Link>
              <Link to="/register" className="text-sm border border-[#0077B6] text-[#0077B6] px-5 py-2 hover:bg-[#0077B6] hover:text-white transition-colors" data-testid="tutorial-register-btn">Register Free</Link>
            </div>
            <Link to="/tutorials" className="block mt-6 text-xs text-zinc-500 hover:text-zinc-300">← Back to tutorials</Link>
          </div>
        </div>
      );
    }
  }

  const renderUrl = `${API}/tutorials/${slug}/render`;

  return (
    <div data-testid="tutorial-view-page" className="min-h-screen bg-[#0A0A0A] text-white">
      <SEO
        title={meta?.title || 'Tutorial'}
        description={meta?.description || 'Interactive AI/ML tutorial at Institute of Analytics USA.'}
        path={`/tutorials/${slug}`}
        type="article"
        noindex={meta?.visibility === 'private'}
      />
      <div className="max-w-6xl mx-auto px-6 py-8">
        <button onClick={() => navigate('/tutorials')} className="text-sm text-zinc-400 hover:text-white flex items-center gap-1 mb-4" data-testid="tutorial-back-link">
          <ArrowLeft className="w-4 h-4" /> All tutorials
        </button>
        <div className="flex items-start justify-between gap-4 mb-6 flex-wrap">
          <div>
            <h1 className="text-3xl sm:text-4xl font-light text-white mb-2" style={{ fontFamily: 'Cabinet Grotesk, sans-serif' }}>{meta?.title}</h1>
            {meta?.description && <p className="text-sm text-zinc-400 max-w-3xl leading-relaxed">{meta.description}</p>}
            {meta?.tags && meta.tags.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-3" data-testid="tutorial-view-tags">
                {meta.tags.map((tag, i) => (
                  <span key={i} className="text-xs font-mono px-2 py-0.5 bg-[#F59E0B]/15 text-[#F59E0B] border border-[#F59E0B]/30">{tag}</span>
                ))}
              </div>
            )}
          </div>
          <VisibilityBadge v={meta?.visibility} />
        </div>

        {showLockedToast && (
          <div data-testid="tutorial-locked-toast" className="mb-4 p-3 bg-amber-900/30 border border-amber-600/60 text-amber-200 text-sm flex items-center justify-between gap-3">
            <span>🔒 That section is part of the full tutorial. Sign in to unlock it.</span>
            <Link to="/login" className="brand-btn text-xs px-3 py-1.5">Sign in</Link>
          </div>
        )}

        <div className="border border-[#262626] bg-[#111111] overflow-hidden">
          <iframe
            ref={iframeRef}
            data-testid="tutorial-iframe"
            src={renderUrl}
            title={meta?.title}
            sandbox="allow-scripts allow-popups allow-same-origin allow-forms"
            onLoad={onIframeLoad}
            style={{ width: '100%', height: `${iframeHeight}px`, border: '0', display: 'block', background: '#fff' }}
          />
        </div>
      </div>
    </div>
  );
}
